import React, { useRef } from "react";
import type Page from "../models/Page";
import type Room from "../models/Room";
import Browser from "../browser";
import HaptikFeedback from "./HaptikFeedback";
import { useEventListener } from "../use/useEventListener";
import Logger from "../Logger";
import { DrawingItemCategory } from "../models/DrawingItemTemplate";
import type Floor from "../models/Floor";
import { EventKey } from "../eventLogger/EventKeys";
const logger = new Logger();

export function ToolView(props: {
  index: number;
  data: string;
  children?: any;
  disabled: boolean;
  imgSrc?: string;
  roomId?: string;
  styleOuter?: any;
  styleName?: string;
}): any {
  const { index, children, data, disabled, imgSrc, styleOuter, styleName, roomId } = props;

  const toolRef = useRef<HTMLDivElement>(null);
  const ignoreTouch = useRef(false);

  const hapticFeedBackSendOnce = useRef<boolean>(false);
  const imgeCreated = useRef<boolean>(false);
  const enteredDrawingAreaOnce = useRef<boolean>(false);

  function onTouchStart(ev: any) {
    hapticFeedBackSendOnce.current = false;
    imgeCreated.current = false;
    ignoreTouch.current = false;
    enteredDrawingAreaOnce.current = false;

    if (toolRef.current) {
      toolRef.current.style.touchAction = "none";
    }

    logger.log("onTouchStart");
  }

  function onTouchMove(ev: any) {
    if (touchMoveItem) {
      if (ev.touches.length != 1) {
        clearTouchFeedback();
        ignoreTouch.current = true;
        return;
      }

      const p = {
        x: ev.touches[0].clientX,
        y: ev.touches[0].clientY,
      };

      const targetElemnt = document.elementFromPoint(p.x, p.y);

      p.y -= Browser.yOffsetTouch();
      p.x -= Browser.xOffsetTouch();

      if (toolRef.current) {
        if (touchMoveContent) {
          touchMoveContent.style.display = "flex"; // no itx
        }

        const STARTSCALE = 5;

        if (touchMoveContent && imgSrc && !imgeCreated.current) {
          imgeCreated.current = true;
          const img = document.createElement("img") as HTMLImageElement;
          img.src = imgSrc;
          img.id = "toolDragImage"; // no itx

          img.style.backgroundColor = "orange";
          img.style.opacity = "0.5";
          img.style.transform =
            `translate(${Browser.xOffsetTouch()}px, ${Browser.yOffsetTouch()}px)` + ` scale(${STARTSCALE}) `;

          img.style.width = `20px`;
          img.style.height = `20px`;

          img.style.transition = "all 1s ease-out";
          touchMoveContent.replaceChildren(img);
        }

        const dr = targetElemnt?.closest("#WorkingPanes"); // no itx
        if (dr) {
          // ev.preventDefault(); not allow if scrolling is in progess !!
          ev.stopPropagation();
          enteredDrawingAreaOnce.current = true;

          if (!hapticFeedBackSendOnce.current) {
            hapticFeedBackSendOnce.current = true;
            toolRef.current.style.touchAction = "none";

            const img = document.getElementById("toolDragImage");

            if (img) {
              setTimeout(() => {
                img.style.opacity = "1";
                img.style.backgroundColor = "transparent";
                img.style.transform = "scale(1) translate(0,0)"; // no itx
              }, 200);
            }

            HaptikFeedback.dragStart();
          }
        } else {
          if (enteredDrawingAreaOnce.current) {
            if (touchMoveContent) {
              touchMoveContent.style.display = "none";
            }
          }
        }
      }

      touchX = p.x;
      touchY = p.y;

      touchMoveItem.style.left = `${p.x}px`;
      touchMoveItem.style.top = `${p.y}px`;
      touchMoveItem.style.display = "inherit";
    }
  }

  function onTouchEnd(ev: any) {
    ev.preventDefault();
    ev.stopPropagation();
    if (toolRef.current) {
      toolRef.current.style.touchAction = "none";
    }

    const targetElemnt = document.elementFromPoint(touchX, touchY);

    const myEvent = new CustomEvent(
      "simulatedDrop", // no itx
      {
        detail: {
          target: targetElemnt,
          clientX: touchX,
          clientY: touchY,
          data: data,
        },

        bubbles: true,
        cancelable: true,
        composed: false,
      },
    );

    targetElemnt?.dispatchEvent(myEvent);

    clearTouchFeedback();
  }

  useEventListener("touchmove", onTouchMove, toolRef, { passive: false });
  useEventListener("touchstart", onTouchStart, toolRef, { passive: false });

  function dragStart(e: any) {
    // const data = JSON.stringify({index: index}
    state.dragData = data;
    e.dataTransfer.setData("text/plain", data);
    const tooldata = JSON.parse(data);

    state.eventLogger.addEvent(EventKey.ToolbarItemUsed, {
      toolId: tooldata.type + "_" + tooldata.category,
      interaction: "drag", // no itx
    });
  }

  // create Consumer via click on Page
  function createConsumerInPage() {
    const tooldata = JSON.parse(data);
    state.eventLogger.addEvent(EventKey.ToolbarItemUsed, {
      toolId: tooldata.type + "_" + tooldata.category,
      interaction: "click", // no itx
    });
    // construct obj
    const consumerData = JSON.parse(data);
    // get the active page and room
    const pageIndex = state.activePaneId;

    let openPage: Page | undefined;
    let openFloorPage: Page | undefined;
    state.allRoomPages().forEach((page, id) => {
      if (id == pageIndex) {
        openPage = page;
      }
    });
    state.allFloorPags().forEach((page, id) => {
      if (id == pageIndex) {
        openPage = page;
        openFloorPage = page;
      }
    });
    if (openPage?.parent?.className == "Floor") {
      openPage = (openPage.parent as Floor).page;
    }
    if (openPage) {
      const room = openPage.parent as Room;

      if (room) {
        const pPX = openPage.findFreePositionForNewObject();
        //create this Consumer in Room
        if (consumerData.category == DrawingItemCategory.Button) {
          const button = openPage.createNewButtonInPage(room, consumerData, pPX);
          state.selectionManager.selectedObject = button;
        } else if (consumerData.category == DrawingItemCategory.Memo) {
          openPage.createNewMemoInPage(room, consumerData, pPX).then((memo) => {
            state.selectionManager.selectedObject = memo;
          });
        } else if (consumerData.category == DrawingItemCategory.Room) {
          if (openFloorPage) {
            const pointForRoom = openFloorPage.findFreePositionForNewRoom();
            const roomGiven = state.objectRegistry.getObject(consumerData.tag);
            if (roomGiven?.className == "Room") {
              openFloorPage.createRoomInPage(roomGiven as Room, pointForRoom);
              openFloorPage.parent?.raiseOnChange("rooms");
            } else {
              openPage.createNewRoomInPage(room, consumerData, pointForRoom);
            }
          }
        } else {
          openPage.createNewConsumerInPage(room, consumerData, pPX).then((consumer) => {
            state.selectionManager.selectedObject = consumer;
          });
        }
      }
    }
  }

  const touchMoveContainer: HTMLElement | null = document.getElementById("touchMoveContainer");

  const touchMoveItem: HTMLElement | null = document.getElementById("touchMoveItem");
  const touchMoveContent: HTMLElement | null = document.getElementById("touchMoveItemContent");

  let touchX = 0;
  let touchY = 0;

  function clearTouchFeedback() {
    if (touchMoveContent) {
      touchMoveContent.innerHTML = "";
    }

    if ((toolRef as any).releasePointerCapture) {
      (toolRef as any).releasePointerCapture();
    }
    if (touchMoveItem) {
      touchMoveItem.style.display = "inherit";
    }
  }

  const consumerData = JSON.parse(data);

  return (
    <div className={roomId ? "toolRoom" : "tool"} ref={toolRef} style={{ ...styleOuter }}>
      <div
        id={roomId ? roomId : consumerData.category == 0 ? "Button" : consumerData.type} // no itx
        className={`toolInner${roomId ? "Room" : ""} ${disabled ? "disabled" : ""}`}
        draggable
        onDragStart={(ev) => dragStart(ev)}
        onDragEnd={() => {
          state.dragData = undefined;
        }}
        onClick={createConsumerInPage}
        ///////////////////////////////////////////////////////
        onTouchEnd={onTouchEnd}
      >
        {children}

        {children === undefined && <div>T{index})</div>}
      </div>
    </div>
  );
}
